import { UnstyledButton } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { useState } from "react";

import { getGetAssetQueryOptions } from "../../../api/generated/endpoints/assets/assets";
import { useListPromptTemplates } from "../../../api/generated/endpoints/generation/generation";
import type { PromptTemplate } from "../../../api/generated/model";
import { pictureOf, type PickedImage } from "../../../platform/media/ImagePicker";
import { templatePictures, usablePicture } from "../../../platform/studio/image/prompt-templates";
import classes from "./simple.module.css";

/** How many of the brand's prompt templates show as suggestions. */
const SHOWN = 8;

interface SuggestionsProps {
  teamId: string;
  brandId: string;
  /** As the composer panel's Apply: the prompt, the template's pictures and its id. */
  onApply: (prompt: string, images: PickedImage[], templateId: string) => void;
}

/**
 * The brand's prompt templates under the prompt, its own first, then the global ones, by name. A
 * chip applies one as the composer's panel does: its pictures are read (from the cache where the
 * page holds them) and only those the composer can still take go in.
 */
export function Suggestions({ teamId, brandId, onApply }: SuggestionsProps) {
  const queryClient = useQueryClient();
  const [reading, setReading] = useState<string | null>(null);
  const custom = useListPromptTemplates(teamId, brandId, { kind: "custom", limit: SHOWN }).data;
  const global = useListPromptTemplates(teamId, brandId, { kind: "global", limit: SHOWN }).data;
  if (custom === undefined || global === undefined) return null;
  const shown = [...custom.items, ...global.items].slice(0, SHOWN);
  if (shown.length === 0) return null;
  const apply = async (template: PromptTemplate) => {
    setReading(template.id);
    try {
      const assets = await Promise.all(
        templatePictures(template).map((id) =>
          queryClient.fetchQuery(getGetAssetQueryOptions(teamId, brandId, id)).catch(() => null),
        ),
      );
      const images = assets.flatMap((asset) =>
        asset !== null && usablePicture(asset)
          ? [{ assetId: asset.id, url: pictureOf(asset), original: asset.urls.original, name: asset.name }]
          : [],
      );
      onApply(template.prompt, images, template.id);
    } finally {
      setReading(null);
    }
  };
  return (
    <fieldset className={classes.suggestions} aria-label="Suggestions">
      <span className={classes.lab}>
        <Sparkles size={14} aria-hidden /> Try
      </span>
      {shown.map((template) => (
        <UnstyledButton
          key={template.id}
          className={classes.chip}
          disabled={reading !== null}
          onClick={() => void apply(template)}
        >
          <span className={classes.chipText}>{template.name}</span>
        </UnstyledButton>
      ))}
    </fieldset>
  );
}
